import { Suspense, useEffect } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from './auth/session.tsx';
import { SyncHost } from './offline/sync-host.tsx';
import { Layout } from './components/Layout.tsx';
import { Empty, PageTitle, Splash, ToastProvider, UpdateBanner } from './components/ui.tsx';
import { loader, screen as named } from './lib/lazy-screen.tsx';
import { listenFinancialViews } from './lib/invalidate.ts';
import { GatePage, LoginPage } from './pages/LoginPage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
// Not lazy: the page removes the link token from the address bar, which must not wait for (or depend on) a chunk
// download. About 2.4 kB compressed.
import { SetPasswordPage } from './pages/auth/SetPasswordPage.tsx';

// Code splitting by module: the login and the dashboard load first; the other screens arrive on demand
// (all chunks are precached by the service worker, so offline navigation still works). See lib/lazy-screen.tsx.
const work = loader(() => import('./pages/WorkPages.tsx'));
const finance = loader(() => import('./pages/FinancePages.tsx'));
const reports = loader(() => import('./pages/ReportPages.tsx'));
const admin = loader(() => import('./pages/AdminPages.tsx'));
const movement = loader(() => import('./pages/movement/MovementPage.tsx'));
const forgot = loader(() => import('./pages/auth/ForgotPasswordPage.tsx'));
const privacy = loader(() => import('./pages/PrivacyPage.tsx'));
const imports = loader(() => import('./pages/imports/ImportsPage.tsx'));
const importBatch = loader(() => import('./pages/imports/BatchPage.tsx'));
const importProfiles = loader(() => import('./pages/imports/ProfilesPage.tsx'));
const quality = loader(() => import('./pages/quality/QualityPage.tsx'));
const tasks = loader(() => import('./pages/tasks/TasksPage.tsx'));
const reconciliationAssist = loader(() => import('./pages/reconciliation/ReconciliationAssistPage.tsx'));
const leakage = loader(() => import('./pages/leakage/LeakagePage.tsx'));
const systemHealth = loader(() => import('./pages/SystemHealthPage.tsx'));
const closing = loader(() => import('./pages/closing/ClosingPage.tsx'));
const analyticsIndex = loader(() => import('./pages/analytics/AnalyticsIndexPage.tsx'));
const anomalies = loader(() => import('./pages/analytics/AnomaliesPage.tsx'));
const forecastStatus = loader(() => import('./pages/analytics/ForecastStatusPage.tsx'));
const scenario = loader(() => import('./pages/analytics/ScenarioPage.tsx'));
const assistant = loader(() => import('./pages/analytics/AssistantPage.tsx'));
const calendar = loader(() => import('./pages/analytics/CalendarPage.tsx'));
const comparativesAdjusted = loader(() => import('./pages/analytics/ComparativesAdjustedPage.tsx'));
const help = loader(() => import('./tour/HelpCenterPage.tsx'));

const MovementPage = named(movement, 'MovementPage');
const AlertsPage = named(work, 'AlertsPage');
const ApprovalsPage = named(work, 'ApprovalsPage');
const ExceptionsPage = named(work, 'ExceptionsPage');
const SyncPage = named(work, 'SyncPage');
const DepositsPage = named(finance, 'DepositsPage');
const ExpensesPage = named(finance, 'ExpensesPage');
const ReconciliationPage = named(finance, 'ReconciliationPage');
const StatementsPage = named(finance, 'StatementsPage');
const ComparativesPage = named(reports, 'ComparativesPage');
const DailyPage = named(reports, 'DailyPage');
const DrePage = named(reports, 'DrePage');
const ExceptionsReportPage = named(reports, 'ExceptionsReportPage');
const AgreementsReportPage = named(reports, 'AgreementsReportPage');
const ReconciliationReportPage = named(reports, 'ReconciliationReportPage');
const MonthlyPage = named(reports, 'MonthlyPage');
const RevenuePage = named(reports, 'RevenuePage');
const WeeklyPage = named(reports, 'WeeklyPage');
const AdminPage = named(admin, 'AdminPage');
const AuditPage = named(admin, 'AuditPage');
const CatalogPage = named(admin, 'CatalogPage');
const ForgotPasswordPage = named(forgot, 'ForgotPasswordPage');
const PrivacyPage = named(privacy, 'PrivacyPage');
const PublicPrivacyPage = named(privacy, 'PublicPrivacyPage');
const ImportsPage = named(imports, 'ImportsPage');
const ImportBatchPage = named(importBatch, 'ImportBatchPage');
const ProfilesPage = named(importProfiles, 'ProfilesPage');
const QualityPage = named(quality, 'QualityPage');
const TasksPage = named(tasks, 'TasksPage');
const ReconciliationAssistPage = named(reconciliationAssist, 'ReconciliationAssistPage');
const LeakagePage = named(leakage, 'LeakagePage');
const SystemHealthPage = named(systemHealth, 'SystemHealthPage');
const ClosingPage = named(closing, 'ClosingPage');
const AnalyticsIndexPage = named(analyticsIndex, 'AnalyticsIndexPage');
const AnomaliesPage = named(anomalies, 'AnomaliesPage');
const ForecastStatusPage = named(forecastStatus, 'ForecastStatusPage');
const ScenarioPage = named(scenario, 'ScenarioPage');
const AssistantPage = named(assistant, 'AssistantPage');
const CalendarPage = named(calendar, 'CalendarPage');
const ComparativesAdjustedPage = named(comparativesAdjusted, 'ComparativesAdjustedPage');
const HelpCenterPage = named(help, 'HelpCenterPage');

function NotFound() {
  return (
    <div>
      <PageTitle>Página não encontrada</PageTitle>
      <Empty>
        O endereço não corresponde a nenhuma tela. <Link to="/">Voltar ao início</Link>
      </Empty>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    // Financial screens show server truth: no stale cache across views, no silent retries of writes.
    queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: true },
    mutations: { retry: false },
  },
});

function Routed() {
  const { session, loading, offlineIdentity } = useSession();
  const setPassword = useMatch('/definir-senha');
  const forgotPassword = useMatch('/esqueci-a-senha');
  const privacy = useMatch('/privacidade');
  // The set-password link is authorized by its token, not by a session: it opens either way, and without waiting
  // for the session check, so the token leaves the address bar at once.
  if (setPassword) return <SetPasswordPage />;
  if (loading) return <Splash />;
  // The privacy notice (LGPD) is readable before signing in and during the access steps (password, second factor).
  if (privacy && (!session || session.gates.length > 0)) {
    return (
      <Suspense fallback={<Splash what="Carregando tela" />}>
        <PublicPrivacyPage />
      </Suspense>
    );
  }
  if (!session) {
    return forgotPassword ? (
      <Suspense fallback={<Splash what="Carregando tela" />}>
        <ForgotPasswordPage />
      </Suspense>
    ) : (
      <LoginPage />
    );
  }
  if (forgotPassword) return <Navigate to="/" replace />;
  if (session.gates.length > 0) return <GatePage />;
  return (
    <SyncHost>
      <Suspense fallback={<Splash what="Carregando tela" />}>
        <Routes>
          <Route element={<Layout />}>
            <Route
              index
              element={offlineIdentity ? <Navigate to="/movimento" replace /> : <DashboardPage />}
            />
            <Route path="movimento" element={<MovementPage />} />
            <Route path="movimento/:date" element={<MovementPage />} />
            <Route path="aprovacoes" element={<ApprovalsPage />} />
            <Route path="excecoes" element={<ExceptionsPage />} />
            <Route path="alertas" element={<AlertsPage />} />
            <Route path="pendencias" element={<TasksPage />} />
            <Route path="fechamento/:movementId" element={<ClosingPage />} />
            <Route path="sincronizacao" element={<SyncPage />} />
            <Route path="conciliacao" element={<ReconciliationPage />} />
            <Route path="conciliacao/assistida" element={<ReconciliationAssistPage />} />
            <Route path="extratos" element={<StatementsPage />} />
            <Route path="depositos" element={<DepositsPage />} />
            <Route path="despesas" element={<ExpensesPage />} />
            <Route path="importacao" element={<ImportsPage />} />
            <Route path="importacao/lotes/:id" element={<ImportBatchPage />} />
            <Route path="importacao/perfis" element={<ProfilesPage />} />
            <Route path="relatorios/receitas" element={<RevenuePage />} />
            <Route path="relatorios/diario" element={<DailyPage />} />
            <Route path="relatorios/semanal" element={<WeeklyPage />} />
            <Route path="relatorios/mensal" element={<MonthlyPage />} />
            <Route path="relatorios/comparativos" element={<ComparativesPage />} />
            <Route path="relatorios/conciliacao" element={<ReconciliationReportPage />} />
            <Route path="relatorios/excecoes" element={<ExceptionsReportPage />} />
            <Route path="relatorios/convenios" element={<AgreementsReportPage />} />
            <Route path="relatorios/dre" element={<DrePage />} />
            <Route path="qualidade" element={<QualityPage />} />
            <Route path="vazamento" element={<LeakagePage />} />
            <Route path="analises" element={<AnalyticsIndexPage />} />
            <Route path="analises/anomalias" element={<AnomaliesPage />} />
            <Route path="analises/previsao" element={<ForecastStatusPage />} />
            <Route path="analises/simulador" element={<ScenarioPage />} />
            <Route path="analises/assistente" element={<AssistantPage />} />
            <Route path="analises/calendario" element={<CalendarPage />} />
            <Route path="analises/comparativos" element={<ComparativesAdjustedPage />} />
            <Route path="saude-do-sistema" element={<SystemHealthPage />} />
            <Route path="auditoria" element={<AuditPage />} />
            <Route path="cadastros" element={<CatalogPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="privacidade" element={<PrivacyPage />} />
            <Route path="ajuda" element={<HelpCenterPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </SyncHost>
  );
}

export function App() {
  // A change made in another tab of this browser (approval, import) refreshes the views open here.
  useEffect(() => listenFinancialViews(queryClient), []);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <BrowserRouter>
            <Routed />
          </BrowserRouter>
        </SessionProvider>
        <UpdateBanner />
      </ToastProvider>
    </QueryClientProvider>
  );
}
